import type { BackupHealth } from '@/api/backup';
import { fmtAge } from '@/lib/rmm-helpers';
import { cn } from '@/lib/utils';

/** Shared Backup-module chips. Status is never colour alone — every chip carries its label. */

const HEALTH_CLASS: Record<BackupHealth, string> = {
  healthy: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  failing: 'bg-destructive/10 text-destructive',
  unknown: 'bg-muted text-muted-foreground',
};
export const HEALTH_LABEL: Record<BackupHealth, string> = { healthy: 'Healthy', warning: 'Issues', failing: 'Failing', unknown: 'No data' };

export function HealthBadge({ health, label, title }: { health: BackupHealth; label?: string; title?: string }) {
  return (
    <span title={title} className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold', HEALTH_CLASS[health])}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />{label ?? HEALTH_LABEL[health]}
    </span>
  );
}

/** Exchange · OneDrive · SharePoint · Teams, one compact chip each. */
export function AppChips({ apps }: { apps: { app: string; health: BackupHealth; lastFullyProtected: string | null }[] }) {
  if (!apps.length) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {apps.map((a) => (
        <span key={a.app}
          title={`${a.app}: ${HEALTH_LABEL[a.health]}${a.lastFullyProtected ? ` · fully protected ${fmtAge(a.lastFullyProtected)} ago` : ' · not fully protected in 7+ days'}`}
          className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium', HEALTH_CLASS[a.health])}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />{a.app}
        </span>
      ))}
    </div>
  );
}

const SEAT_STATE_CLASS: Record<string, string> = {
  Active: 'bg-success-soft text-success',
  Paused: 'bg-warning-soft text-warning',
  Archived: 'bg-muted text-muted-foreground',
  Unprotected: 'bg-destructive/10 text-destructive',
};

export function SeatStateBadge({ state }: { state: string | null }) {
  if (!state) return <span className="rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10.5px] font-semibold text-destructive">No seat</span>;
  return <span className={cn('rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold', SEAT_STATE_CLASS[state] ?? 'bg-muted text-muted-foreground')}>{state}</span>;
}

export const SEAT_TYPE_LABEL: Record<string, string> = {
  User: 'User', SharedMailbox: 'Shared mailbox', Site: 'Site', TeamSite: 'Team site', Team: 'Team', SharedDrive: 'Shared drive',
};
